import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import {
    Button,
    Card,
    FieldError,
    Form,
    Input,
    Label,
    TextField,
} from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { NewBranchError } from '../../useBranchStep';

type NewBranchCardProps = {
    isOpen: boolean;
    onOpen: () => void;
    onCancel: () => void;
    name: string;
    onNameChange: (value: string) => void;
    address: string;
    onAddressChange: (value: string) => void;
    error: NewBranchError | null;
    isCreating: boolean;
    onCreate: () => void;
};

/**
 * "Add a branch": closed, a card-wide button; open, the same card holds the
 * name and address fields with Create and Cancel. Inline on purpose: the
 * wizard never opens the branch drawer. A missing field shows on its field;
 * a server refusal or a failed call shows under the name in the app's own
 * words, never the server's sentence, and the typed values stay.
 */
export const NewBranchCard: React.FC<NewBranchCardProps> = ({
    isOpen,
    onOpen,
    onCancel,
    name,
    onNameChange,
    address,
    onAddressChange,
    error,
    isCreating,
    onCreate,
}) => {
    const { t } = useTranslation('pages');

    if (!isOpen) {
        return (
            <Card
                className="p-0"
                variant="transparent"
                {...getNodeTestIdProp('onboarding-branch-new')}
            >
                <Button
                    className="w-full justify-start"
                    onPress={onOpen}
                    size="lg"
                    variant="tertiary"
                    {...getNodeTestIdProp('onboarding-branch-new-open')}
                >
                    <Icon aria-hidden icon="gravity-ui:plus" />
                    {t('onboarding.branch.add')}
                </Button>
            </Card>
        );
    }

    return (
        <Card {...getNodeTestIdProp('onboarding-branch-new')}>
            <Form
                className="flex flex-col gap-4"
                onSubmit={(event) => {
                    event.preventDefault();
                    onCreate();
                }}
                validationBehavior="aria"
            >
                <Card.Header>
                    <Card.Title>{t('onboarding.branch.add')}</Card.Title>
                </Card.Header>
                <Card.Content className="flex flex-col gap-4">
                    <TextField
                        autoFocus
                        fullWidth
                        isDisabled={isCreating}
                        isInvalid={
                            error?.kind === 'nameRequired' ||
                            error?.kind === 'server'
                        }
                        maxLength={200}
                        onChange={onNameChange}
                        value={name}
                        variant="secondary"
                        {...getNodeTestIdProp('onboarding-branch-new-name')}
                    >
                        <Label>{t('onboarding.branch.nameLabel')}</Label>
                        <Input />
                        {error?.kind === 'nameRequired' ? (
                            <FieldError>
                                {t('onboarding.branch.nameRequired')}
                            </FieldError>
                        ) : null}
                        {error?.kind === 'server' ? (
                            <FieldError
                                {...getNodeTestIdProp(
                                    'onboarding-branch-new-error',
                                )}
                            >
                                {error.failure === 'refused'
                                    ? t('onboarding.branch.createRefused')
                                    : t('onboarding.branch.createFailed')}
                            </FieldError>
                        ) : null}
                    </TextField>
                    <TextField
                        fullWidth
                        isDisabled={isCreating}
                        isInvalid={error?.kind === 'addressRequired'}
                        maxLength={500}
                        onChange={onAddressChange}
                        value={address}
                        variant="secondary"
                        {...getNodeTestIdProp('onboarding-branch-new-address')}
                    >
                        <Label>{t('onboarding.branch.addressLabel')}</Label>
                        <Input />
                        {error?.kind === 'addressRequired' ? (
                            <FieldError>
                                {t('onboarding.branch.addressRequired')}
                            </FieldError>
                        ) : null}
                    </TextField>
                </Card.Content>
                <Card.Footer className="flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <Button
                        className="w-full sm:w-auto"
                        isDisabled={isCreating}
                        onPress={onCancel}
                        variant="tertiary"
                        {...getNodeTestIdProp('onboarding-branch-new-cancel')}
                    >
                        {t('onboarding.branch.cancel')}
                    </Button>
                    <Button
                        className="w-full sm:w-auto"
                        isPending={isCreating}
                        type="submit"
                        variant="secondary"
                        {...getNodeTestIdProp('onboarding-branch-new-create')}
                    >
                        {t('onboarding.branch.create')}
                    </Button>
                </Card.Footer>
            </Form>
        </Card>
    );
};
